import React, { forwardRef } from 'react';
import { Button, type ButtonProps } from './Button';
import { cn } from '@/lib/utils';

export type IconButtonSize = 'sm' | 'md' | 'touch';
export type IconButtonTone = 'neutral' | 'danger';

export interface IconButtonProps
  extends Omit<ButtonProps, 'children' | 'size' | 'startIcon' | 'endIcon' | 'aria-label'> {
  /** Accessible action name. Also becomes the native tooltip unless tooltip is supplied. */
  label: string;
  icon: React.ReactNode;
  size?: IconButtonSize;
  tone?: IconButtonTone;
  tooltip?: string;
}

const sizeMap: Record<IconButtonSize, NonNullable<ButtonProps['size']>> = {
  sm: 'icon-sm',
  md: 'icon-md',
  touch: 'icon-touch',
};

/**
 * Canonical icon-only action.
 *
 * Use sm in dense rows/toolbars, md in page and drawer chrome, and touch on
 * touch-first surfaces. A required label prevents inaccessible unlabeled icons.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
  label,
  icon,
  size = 'md',
  tone = 'neutral',
  tooltip,
  className,
  variant,
  ...props
}, ref) => (
  <Button
    ref={ref}
    type="button"
    aria-label={label}
    title={tooltip ?? label}
    variant={variant ?? (tone === 'danger' ? 'destructive-ghost' : 'ghost')}
    size={sizeMap[size]}
    className={cn('shrink-0', className)}
    startIcon={icon}
    {...props}
  />
));

IconButton.displayName = 'IconButton';

export default IconButton;
